/* eslint-disable no-console */
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckOutlined,
  CloseOutlined
} from '@ant-design/icons';
import {Button, Steps, Drawer, Flex, Form, Row, Col, Image} from 'antd';
import React, {useEffect, useRef, useState} from 'react';

import {detectAutoDetect, drawerZIndex} from '../../../constants/Common';
import {
  PollingDBStatus,
  PollingStatusMessages,
  StepState
} from '../../../pages/aiDocumentManager/Constants/commonConstants';
import PdfRender from '../../../pages/OCR/PDFRender';
import {APIType, APITypeEnum} from '../../../services/interface/tableAPIType';
import DocumentOCRStatusPollingAPI from '../../../services/modules/AIDocumentManager/DocumentOCRStatusPollingAPI';
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';
import useUserStore from '../../../store/userStore/userStore';
import showMessage from '../../../utils/showMessage';
import DocumentUploadTemplate from '../../DocumentUploadTemplate/DocumentUploadTemplate';
import Loader from '../../Loader/Loader';

import MySvgComponent from './StepIconDrawer';

interface FromComponentProps {
  currentStep?: number;
  onStepsChange?: (steps: string[]) => void;
  props?: any;
}

interface ModalFormProps {
  open: boolean;
  onCancel: () => void;
  FormComponent: React.ReactElement;
  initialValue?: any;
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  API?: (
    type: APIType,
    shouldCall?: boolean | any,
    data?: any,
    id?: string
  ) => Promise<any>;
  data?: any;
  type?: {key: string; value: string} | undefined;
  buttonDisabled?: {
    value: boolean;
    setter: React.Dispatch<React.SetStateAction<boolean>>;
  };
  fullScreen?: boolean;
  stepsTitle: any[];
  stepsType: any[];
  isChecker?: boolean;
  isAction?: boolean;
  hasOCRProcessing: boolean;
  efdbData?: any[];
  allFacilities?: any[];
}

const DocumentUploadDrawerForm: React.FC<ModalFormProps> = ({
  open,
  onCancel,
  API,
  initialValue,
  data = undefined,
  FormComponent,
  setRefresh,
  buttonDisabled = null,
  stepsTitle,
  stepsType,
  isChecker = false,
  efdbData = [],
  allFacilities = []
}) => {
  const [form] = Form.useForm();
  const [caform] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const companyId = useCompanyStore.getState().companyId;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState(data);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isSaveAPICalled, setIsSaveAPICalled] = useState(false);

  const [steps, setSteps] = useState<string[]>([]);

  useEffect(() => {
    setSteps(stepsTitle);
  }, [stepsTitle]);

  const userDetails = useUserStore.getState().userDetails;

  const [isLoading, setIsLoading] = useState(true);

  const [pollingId, setPollingId] = useState<string | null>(null);
  const [pollingStartTime, setPollingStartTime] = useState<string>();
  const maxAttempts = 6;
  const pollInterval = 5000; // Poll every 5 seconds
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!pollingId) return;

    let attemptCount = 0;

    const pollApi = () => {
      if (attemptCount >= maxAttempts) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        form.setFieldValue('pollingStatus', PollingDBStatus.failed);
        // next();
        showMessage({
          message: PollingStatusMessages.processingFailed,
          status: 'error'
        });
        setIsSubmitted(false);

        return;
      }

      attemptCount++;

      DocumentOCRStatusPollingAPI(pollingId)
        .then((res) => {
          if (res.status === PollingDBStatus.ready_for_review) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              setFormData(res?.data);
              form.setFieldValue(
                'pollingStatus',
                PollingDBStatus.ready_for_review
              );
              caform.resetFields();
              next();
            }
          } else if (res.status === PollingDBStatus.failed) {
            if (intervalRef.current) clearInterval(intervalRef.current); // Stop on error
            form.setFieldValue('pollingStatus', PollingDBStatus.failed);
            showMessage({
              message: PollingStatusMessages.processingFailed,
              status: 'error'
            });
            setIsSubmitted(false);
          } else {
            form.setFieldValue('pollingStatus', PollingDBStatus.in_progress);
          }
        })
        .catch(() => {
          if (intervalRef.current) clearInterval(intervalRef.current); // Stop on error
          form.setFieldValue('pollingStatus', PollingDBStatus.failed);
          // next();
          showMessage({
            message: PollingStatusMessages.processingFailed,
            status: 'error'
          });
          setIsSubmitted(false);
        });
    };

    // Initial poll
    form.setFieldValue('pollingStatus', PollingDBStatus.in_progress);
    pollApi();

    // Set interval
    intervalRef.current = setInterval(pollApi, pollInterval);

    // Cleanup on unmount or when pollingId changes (stops previous polling)
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [pollingStartTime]);

  useEffect(() => {
    if (open) {
      setCurrentStep(isChecker ? stepsTitle.length - 1 : 0);
    }
  }, [open, isChecker, stepsTitle.length]);

  useEffect(() => {
    setFormData(data);
    setIsSubmitDisabled(true);
    form.resetFields();
    if (formData !== undefined) {
      form.setFieldsValue(formData);
      setIsLoading(false);
    } else {
      form.resetFields(initialValue);
      setIsLoading(false);
    }
  }, [open, data]);

  const {Step} = Steps;

  const isLast = steps.length - 1;

  const next = () => {
    setIsSubmitted(false);
    setPollingId(null);
    form
      .validateFields()
      .then(() => {
        setCurrentStep(currentStep + 1);
      })
      .catch((errorInfo) => {
        if (errorInfo.errorFields && errorInfo.errorFields.length > 0) {
          form.scrollToField(errorInfo.errorFields[0].name);
        }
      });
  };

  const prev = () => {
    setPollingId(null);
    setCurrentStep(currentStep - 1);
  };

  const handleResponse = (res: any) => {
    if (res.status === 'error' || res === undefined) {
      showMessage(res);
      if (buttonDisabled) {
        buttonDisabled.setter(false);
      }
    } else if (res.status === 'success') {
      showMessage(res);

      if (currentStep === isLast) {
        onCancel();
        setRefresh(true);
        setIsSubmitted(false);
        if (buttonDisabled) {
          buttonDisabled.setter(true);
        }
      } else {
        setFormData(res?.data);
        setIsSubmitDisabled(true);
        form.setFieldsValue(res?.data);

        if (res?.data?.status === PollingDBStatus.uploaded) {
          setPollingId(res?.data?._id);
          setPollingStartTime(Date.now().toString());
        } else {
          next();
        }
      }
    } else {
      setIsSubmitted(false);
      if (buttonDisabled) {
        buttonDisabled.setter(false);
      }
    }
  };

  const removeIdForUpdate = (value: any) => {
    const newValue = Object.keys(value).reduce((acc: any, key) => {
      if (key !== '_id' && key !== '__v' && key !== 'key') {
        acc[key] = value[key] === undefined ? null : value[key];
      }

      return acc;
    }, {});

    return newValue;
  };

  const onSubmit = () => {
    const value = form.getFieldsValue(true);

    // Remove pollingStatus before API call
    delete value.pollingStatus;
    delete value.section;

    Object.keys(value).forEach((key) => {
      if (value[key] === undefined) {
        value[key] = null; // Replace with a suitable placeholder value
      }
    });

    value.company = companyId;

    const mainData = formData?._id ? removeIdForUpdate(value) : value;
    if (formData?._id) {
      mainData.updatedBy = userDetails._id;
    } else {
      mainData.created_by = userDetails._id;
      mainData.uploaded_at = userDetails._id;
    }

    if (Array.isArray(mainData?.bill_type)) {
      const splitBillType = mainData?.bill_type;
      mainData.doc_type = splitBillType?.[0];
    }

    if (API) {
      API(
        formData?._id ? APITypeEnum.PUT : APITypeEnum.POST_NEW,
        {
          ...mainData
        },
        formData?._id ? formData?._id : ''
      )
        .then((res) => {
          setIsSaveAPICalled(true);
          handleResponse(res);
        })
        .catch(() => {});
    }
  };

  const onConfirmAndSave = () => {
    setIsSubmitted(false);
    if (stepsType[currentStep] === StepState.WITH_FILE_PREVIEW) {
      caform
        .validateFields()
        .then(() => {
          caform.submit();
          setIsSubmitted(true);
        })
        .catch((errorInfo) => {
          if (errorInfo.errorFields && errorInfo.errorFields.length > 0) {
            form.scrollToField(errorInfo.errorFields[0].name);
          }
        });
    } else {
      form.submit();
      setIsSubmitted(true);
    }
  };

  const onStepClick = (index: number) => {
    if (currentStep < index) {
      form
        .validateFields()
        .then(() => {
          setCurrentStep(index);
        })
        .catch(() => {});
    } else {
      setCurrentStep(index);
    }
  };

  const minTwoDigits = (n: number) => {
    return (n < 10 ? '0' : '') + n;
  };

  const drawerTitle = (
    <Flex
      className="stepsBox"
      style={{
        borderRadius: '0px',
        width: '100%',
        paddingLeft: '20px',
        border: 'none'
      }}
    >
      <Steps current={currentStep}>
        {steps.map((step, index) => {
          const isFinished = currentStep > index;
          const isProcess = currentStep === index;

          return (
            <Step
              key={step}
              title={
                <div
                  style={{
                    whiteSpace: 'pre-line',
                    maxWidth: steps.length >= 3 ? '135px' : '180px'
                  }}
                >
                  {step}
                </div>
              }
              onClick={() => {
                onStepClick(index);
              }}
              style={{
                cursor: 'pointer',
                textAlign: steps.length === 1 ? 'left' : 'center'
              }}
              icon={
                <div
                  className={
                    isFinished
                      ? 'stepIconCircle isFinished'
                      : isProcess
                        ? 'stepIconCircle isProcess'
                        : 'stepIconCircle'
                  }
                >
                  <MySvgComponent
                    number={minTwoDigits(index + 1)}
                    isFinished={isFinished}
                    isProcess={isProcess}
                  />
                </div>
              }
            />
          );
        })}
      </Steps>
    </Flex>
  );

  const drawerFooter = (
    <Flex
      className="drawerButtonContainer"
      style={{borderTop: '1px solid #d2d4da'}}
    >
      <div>
        <Button
          onClick={() => {
            onCancel();
            setIsSubmitted(false);
            if (isSaveAPICalled) {
              setRefresh(true);
            }
            setTimeout(() => {
              setCurrentStep(0);
            }, 1000);
          }}
          className="drawerButton drawerCancel"
        >
          Cancel
          <CloseOutlined />
        </Button>
      </div>
      <div className="rightSideNextDone">
        {currentStep > 0 && (
          <Button
            style={{margin: '0 8px'}}
            onClick={() => {
              prev();
            }}
            className="drawerButton Back"
            disabled={isSubmitted}
          >
            <ArrowLeftOutlined />
            Back
          </Button>
        )}
        {currentStep < isLast &&
          stepsType[currentStep] === StepState.UPLOAD_AND_VALIDATE && (
            <Button
              className="drawerButton NextDone"
              style={{
                width: '200px'
              }}
              disabled={isSubmitted}
              loading={isSubmitted}
              htmlType="submit"
              onClick={() => {
                if (
                  formData?.status !== 'ready_for_review' &&
                  form.getFieldValue('location')?.startsWith('https')
                ) {
                  showMessage({
                    message: 'Please Re-Upload file and try again!',
                    status: 'info'
                  });
                } else {
                  isSubmitDisabled ? next() : onConfirmAndSave();
                }
              }}
            >
              {stepsType[currentStep] === StepState.UPLOAD_AND_VALIDATE
                ? isSubmitted
                  ? 'Uploading...'
                  : 'Upload & Validate'
                : 'Next'}
              <ArrowRightOutlined />
            </Button>
          )}

        {currentStep < isLast &&
          stepsType[currentStep] !== StepState.UPLOAD_AND_VALIDATE && (
            <Button
              className="drawerButton NextDone"
              style={{
                width: '100px'
              }}
              disabled={isSubmitted}
              onClick={() => {
                next();
              }}
            >
              Next
              <ArrowRightOutlined />
            </Button>
          )}

        {currentStep === isLast &&
          stepsType[currentStep] !== StepState.WITH_FILE_PREVIEW && (
            <Button
              type="primary"
              style={{width: '200px'}}
              className="drawerButton NextDone"
              onClick={() => {
                onConfirmAndSave();
              }}
              loading={isSubmitted}
              htmlType="submit"
              disabled={isSubmitDisabled}
            >
              {isChecker ? 'Save' : 'Confirm & Save'}
              <CheckOutlined />
            </Button>
          )}
        {currentStep === isLast &&
          stepsType[currentStep] === StepState.WITH_FILE_PREVIEW &&
          formData?._id && (
            <Button
              type="primary"
              style={{width: '200px'}}
              className="drawerButton NextDone"
              onClick={() => {
                onConfirmAndSave();
              }}
              loading={isSubmitted}
              htmlType="submit"
              disabled={isSubmitDisabled}
            >
              {isChecker ? 'Save' : 'Confirm & Save'}
              <CheckOutlined />
            </Button>
          )}
      </div>
    </Flex>
  );

  return isLoading ? (
    <Loader />
  ) : (
    <Drawer
      placement={'right'}
      size="large"
      width={
        stepsType[currentStep] === StepState.WITH_FILE_PREVIEW ? '80%' : '40%'
      }
      onClose={() => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (isSaveAPICalled) {
          setRefresh(true);
        }
        onCancel();
        setIsSubmitted(false);
        setTimeout(() => {
          setCurrentStep(0);
        }, 1000);
      }}
      destroyOnClose={true}
      zIndex={drawerZIndex}
      open={open}
      closeIcon={false}
      bodyStyle={{
        padding: '0px',
        background: '#F3F4F8',
        height: '100%'
      }}
    >
      <Row style={{height: '100%', marginLeft: 0, marginRight: 0}} gutter={16}>
        {stepsType[currentStep] === StepState.WITH_FILE_PREVIEW &&
        currentStep === isLast ? (
          <Col
            span={12}
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              background: '#fff'
            }}
          >
            <div
              style={{
                overflow: 'auto',
                margin: '20px',
                flex: 1,
                height: '550px',
                display: formData?.location?.endsWith('.pdf') ? '' : 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {formData?.location?.endsWith('.pdf') ? (
                <>
                  <PdfRender
                    pdfUrl={formData?.location}
                    handleImageLoad={() => {}}
                    setPage={() => {}}
                    setImgLoaded={() => {}}
                  />
                </>
              ) : (
                <Image
                  src={formData?.location}
                  preview={true}
                  alt="preview"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
              )}
            </div>
          </Col>
        ) : null}
        <Col
          span={
            stepsType[currentStep] === StepState.WITH_FILE_PREVIEW ? 12 : 24
          }
          className={
            stepsType[currentStep] === StepState.WITH_FILE_PREVIEW ? '' : ''
          }
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderLeft: '1px solid #d2d4da',
            paddingLeft: '0px',
            paddingRight: 0
          }}
        >
          <div className="ant-drawer-header">{drawerTitle}</div>
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px',
              background: 'rgb(243, 244, 248)'
            }}
          >
            <div
              className="bodyPage"
              style={{height: '100%', padding: '0px', overflow: 'auto'}}
            >
              {stepsType[currentStep] !== StepState.WITH_FILE_PREVIEW && (
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={onSubmit}
                  scrollToFirstError={{
                    behavior: 'smooth',
                    block: 'start',
                    scrollMode: 'if-needed'
                  }}
                  onValuesChange={() => {
                    setIsSubmitDisabled(false);
                  }}
                  onFinishFailed={() => {
                    setIsSubmitted(false);
                  }}
                  autoComplete={detectAutoDetect()}
                  className="drawerForm"
                >
                  <div className="formLayout" style={{padding: '15px'}}>
                    {React.cloneElement(
                      FormComponent as React.ReactElement<FromComponentProps>,
                      {
                        currentStep,
                        ...FormComponent.props
                      }
                    )}
                  </div>
                </Form>
              )}

              {stepsType[currentStep] === StepState.WITH_FILE_PREVIEW && (
                <DocumentUploadTemplate
                  isNewRecord={!data}
                  caForm={caform}
                  efdbData={efdbData}
                  allFacilities={allFacilities}
                  record={formData}
                  handleResponse={(res: any) => {
                    setIsSubmitted(false);
                    if (res.status === 'error' || res === undefined) {
                      showMessage(res);
                      if (buttonDisabled) {
                        buttonDisabled.setter(false);
                      }
                    } else if (res.status === 'success') {
                      showMessage(res);
                      onCancel();
                      setRefresh(true);
                      if (buttonDisabled) {
                        buttonDisabled.setter(true);
                      }
                    } else {
                      if (buttonDisabled) {
                        buttonDisabled.setter(false);
                      }
                    }
                  }}
                  onFinishFailed={() => {
                    setIsSubmitted(false);
                    if (buttonDisabled) {
                      buttonDisabled.setter(false);
                    }
                  }}
                  onValuesChange={(status: boolean) => {
                    setIsSubmitDisabled(status);
                  }}
                />
              )}
            </div>
          </div>
          <div className="ant-drawer-footer">{drawerFooter}</div>
        </Col>
      </Row>
    </Drawer>
  );
};

export default DocumentUploadDrawerForm;
